/**
 * Represents a response from the wizard flow, containing step information and navigation metadata.
 * @typedef {import('./../steps/BaseStep').default} BaseStep
 * @typedef {typeof import('./../steps/BaseStep').default} BaseStepConstructor
 */
export class WizardFlowResponse {
  /** @type {BaseStepConstructor|null} The step class constructor or null if not found */
  #step;

  /** @type {number} The index of the step in the flow */
  #index;

  /** @type {number} The index of the first step in the flow */
  #first;

  /** @type {number} The index of the last step in the flow */
  #last;

  /**
   * Creates a new WizardFlowResponse instance.
   * @param {BaseStepConstructor|null} step - The step class constructor or null if the step is not found.
   * @param {number} index - The index of the step in the flow.
   * @param {number} first - The index of the first step in the flow.
   * @param {number} last - The index of the last step in the flow.
   */
  constructor(step, index, first, last) {
    this.#step = step;
    this.#index = index;
    this.#first = first;
    this.#last = last;
  }

  /**
   * Gets the step class constructor.
   * @returns {BaseStepConstructor} The step class constructor.
   */
  get component() {
    if (!this.#step) {
      const msg = `Step not found for index ${this.#index}`;

      console.error(msg);
      throw new Error(msg);
    }

    return this.#step;
  }

  /**
   * Checks if this is the first step in the flow.
   * @returns {boolean} True if this step is the first step, false otherwise.
   */
  get isFirst() {
    return this.#first === this.#index;
  }

  /**
   * Checks if this is the last step in the flow.
   * @returns {boolean} True if this step is the last step, false otherwise.
   */
  get isLast() {
    return this.#last === this.#index;
  }

  /**
   * Checks if the step was found in the flow.
   * @returns {boolean} True if the step exists, false if it's null.
   */
  get isFound() {
    return !!this.#step;
  }

  /**
   * Gets the index of the current step.
   * @returns {number} The index of the step.
   */
  get index() {
    return this.#index;
  }
}
